/**
 * IconButtonTooltips: one instant tooltip for every icon-only button in the app (Antonin, 16 Oct:
 * "instant tooltip on every icon button across the app"). One listener on the document, so the shared
 * pieces (the row and toolbar icon buttons, the DS ones included) get it without a wrapper each and a
 * new icon button gets it for free. It reads the button's accessible name, the one thing every icon
 * button already carries.
 *
 * A button qualifies when it has a name (`aria-label`), draws no visible text (the text inside
 * `aria-hidden` boxes does not count) and shows a glyph. It does NOT qualify when:
 *  - it already carries a DS tooltip (the DS trigger sets `aria-describedby` on its button),
 *  - it is a rail destination (the bridge draws those from the same name),
 *  - it is the rail's wordmark (its name is visible) or opts out with `data-no-tip`. The rail's expand / collapse toggle
 *    is a plain icon button and gets the tooltip (19 Oct).
 *
 * Opens and closes at once: no delay, no fade (design log 13 Oct, Rule "tooltips"); moving between
 * neighbours keeps it up because the next button replaces the text. Keyboard focus shows it too.
 * Look: the bridge's "icon button tooltip" block, on the `--tooltip-*` tokens. The position is
 * handed to the stylesheet as two custom properties (`--tip-x`, `--tip-y`), never an inline style.
 *
 * The same tooltip also shows the full text of a CUT-OFF row in the left patient summary and in the patient chart's tables (Antonin, 19 Oct): text drawn with an
 * ellipsis (`text-overflow: ellipsis`) whose content is wider than its box (`scrollWidth > clientWidth`), inside the pre-visit rail
 * or the started chart column. On hover it is the text under the pointer; on keyboard focus it is the first cut-off text of the
 * row (`li`) the focus is in. Only when really cut: a row that fits shows nothing. An element may carry `data-tip` to say the
 * text itself; otherwise it is its own text. Rows also carry a native `title` with the same words: it is removed while ours
 * shows, so the browser adds no slower second tooltip.
 *
 * Behaviour change (logged in SHOWCASE-CHANGELOG.md): icon buttons and cut-off summary rows gain a tooltip; nothing else moves.
 */
import { useEffect, useLayoutEffect, useRef, useState } from 'react';

const CANDIDATE = 'button[aria-label], [role="button"][aria-label], a[aria-label]';
/** Icon buttons whose words the bridge hides and draws as a Lucide glyph (the left panel's row actions on the DS
 *  `ClinicalTermRow`, which takes text only): they count as icon-only though their text is in the DOM. Only the actions
 *  the bridge draws a glyph for (showcase-bridge.css, "Row actions of the started visit's column"); any other row action
 *  keeps its word and is not an icon button. With `visitNoteV2` (PROD-3414) the encounter's "Problem note" and "Add to note"
 *  are drawn too (showcase-visit-note.css). */
const GLYPH_DRAWN =
  '[data-slot="chart-section"] button[data-size="row"]:is([aria-haspopup="menu"], [aria-label="+ Note"], [aria-label="Resolve"], [aria-label="Reactivate"], [aria-label="Edit"]), ' +
  ':root[data-visit-note-v2="on"] [data-slot="chart-section"] button[data-size="row"]:is([aria-label="Problem note"], [aria-label="Add to note"])';
const EXCLUDED = '.shell-rail > nav > ul, [data-slot="rail-brand"], [data-no-tip]';

/** The button's visible text: everything outside `aria-hidden` boxes. */
function visibleText(el: Element): string {
  let out = '';
  const walk = (node: Node) => {
    if (node.nodeType === Node.TEXT_NODE) {
      out += node.textContent ?? '';
      return;
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return;
    const e = node as Element;
    if (e.getAttribute('aria-hidden') === 'true' || e.tagName.toLowerCase() === 'svg') return;
    e.childNodes.forEach(walk);
  };
  el.childNodes.forEach(walk);
  return out.trim();
}

/** The icon-only button under `target`, or null. */
export function iconButtonFor(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const el = target.closest<HTMLElement>(CANDIDATE);
  if (!el) return null;
  const name = el.getAttribute('aria-label')?.trim();
  if (!name) return null;
  if (el.hasAttribute('aria-describedby')) return null;
  if (el.closest(EXCLUDED)) return null;
  if (!el.querySelector('svg, [aria-hidden="true"]') && !el.matches(GLYPH_DRAWN)) return null;
  if (visibleText(el) !== '' && !el.matches(GLYPH_DRAWN)) return null;
  return el;
}

/** The left patient summary (the pre-visit rail, the DS slot, and the started chart column) and the patient chart page
 *  (a chart table's one-line notes and two-line names; Antonin, 19 Oct). */
const TRUNCATION_SCOPE =
  '[data-slot="pre-encounter-summary-rail"], [data-slot="chart-column"], [data-slot="patient-chart"], [data-slot="encounter-dx-card"]';

/**
 * True when `el` is cut off right now: text drawn with an ellipsis whose content is wider than its box, or text clamped
 * to a number of lines (`-webkit-line-clamp`) whose content is taller than its box.
 */
function isCutOff(el: HTMLElement): boolean {
  const style = getComputedStyle(el);
  if (style.textOverflow === 'ellipsis') return el.scrollWidth > el.clientWidth;
  const clamp = style.getPropertyValue('-webkit-line-clamp');
  if (clamp && clamp !== 'none') return el.scrollHeight > el.clientHeight + 1;
  return false;
}

/**
 * The cut-off text a pointer is over, in the left patient summary: the nearest element from `target` up to the
 * panel that is cut off. Null outside the panel, or when the text fits.
 */
function cutOffTextFor(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element)) return null;
  const scope = target.closest(TRUNCATION_SCOPE);
  if (!scope) return null;
  for (let el: Element | null = target; el && el !== scope; el = el.parentElement) {
    if (el instanceof HTMLElement && isCutOff(el)) return el;
  }
  return null;
}

/** The first cut-off text inside the row (`li`) that holds keyboard focus, in the left patient summary. */
function cutOffTextInRowOf(target: EventTarget | null): HTMLElement | null {
  if (!(target instanceof Element) || !target.closest(TRUNCATION_SCOPE)) return null;
  const row = target.closest('li');
  if (!row) return null;
  for (const el of Array.from(row.querySelectorAll<HTMLElement>('*'))) {
    if (isCutOff(el)) return el;
  }
  return null;
}

/** What a cut-off text says: its own `data-tip`, else its text. */
function cutOffWords(el: HTMLElement): string {
  return (el.getAttribute('data-tip') ?? el.textContent ?? '').trim();
}

interface Tip {
  text: string;
  rect: DOMRect;
}

export function IconButtonTooltips() {
  const [tip, setTip] = useState<Tip | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const show = (el: HTMLElement) => {
      // A DS icon button (the chart row actions) also carries a `title`: the browser would add its own,
      // delayed tooltip under ours. The name is already in `aria-label`, so the `title` goes.
      el.removeAttribute('title');
      setTip({ text: el.getAttribute('aria-label') ?? '', rect: el.getBoundingClientRect() });
    };
    const hide = () => setTip(null);
    /** A cut-off row text: its words, anchored to the text. The row's native `title` goes while ours shows. */
    const showCutOff = (el: HTMLElement) => {
      const text = cutOffWords(el);
      if (!text) return false;
      // The text itself and the boxes around it up to its row (the DS row's wrapper carries the title); never a sibling's.
      for (let a: Element | null = el; a && !a.matches(TRUNCATION_SCOPE); a = a.parentElement) {
        a.removeAttribute('title');
        if (a.tagName === 'LI') break;
      }
      // The diagnosis card is itself the scope and carries the DS's native title with the whole term.
      el.closest('[data-slot="encounter-dx-card"]')?.removeAttribute('title');
      setTip({ text, rect: el.getBoundingClientRect() });
      return true;
    };
    const onOver = (e: PointerEvent) => {
      const el = iconButtonFor(e.target);
      if (el) show(el);
      else {
        const cut = cutOffTextFor(e.target);
        if (!cut || !showCutOff(cut)) hide();
      }
    };
    const onFocus = (e: FocusEvent) => {
      const el = iconButtonFor(e.target);
      if (el && el.matches(':focus-visible')) show(el);
      else if (!el && e.target instanceof Element && e.target.matches(':focus-visible')) {
        const cut = cutOffTextInRowOf(e.target);
        if (cut) showCutOff(cut);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') hide();
    };
    document.addEventListener('pointerover', onOver, true);
    document.addEventListener('pointerdown', hide, true);
    document.addEventListener('focusin', onFocus, true);
    document.addEventListener('focusout', hide, true);
    document.addEventListener('keydown', onKey, true);
    document.addEventListener('scroll', hide, true);
    return () => {
      document.removeEventListener('pointerover', onOver, true);
      document.removeEventListener('pointerdown', hide, true);
      document.removeEventListener('focusin', onFocus, true);
      document.removeEventListener('focusout', hide, true);
      document.removeEventListener('keydown', onKey, true);
      document.removeEventListener('scroll', hide, true);
    };
  }, []);

  // Place it above the button, centred, 4 clear (`--tooltip-gap`); below when there is no room above;
  // kept inside the window sideways. Measured after paint of the text, before the browser draws it.
  useLayoutEffect(() => {
    const node = ref.current;
    if (!tip || !node) return;
    const gap = parseFloat(getComputedStyle(node).getPropertyValue('--tooltip-gap')) || 0;
    const { width, height } = node.getBoundingClientRect();
    const margin = gap;
    let x = tip.rect.left + tip.rect.width / 2 - width / 2;
    x = Math.max(margin, Math.min(x, window.innerWidth - width - margin));
    let y = tip.rect.top - height - gap;
    if (y < margin) y = tip.rect.bottom + gap;
    node.style.setProperty('--tip-x', `${Math.round(x)}px`);
    node.style.setProperty('--tip-y', `${Math.round(y)}px`);
    node.dataset.ready = 'true';
  }, [tip]);

  if (!tip) return null;
  return (
    <div ref={ref} role="tooltip" className="icon-button-tip" data-ready="false">
      {tip.text}
    </div>
  );
}
